/**
 * Theme State Management
 * Redux slice for managing theme mode and color theme preferences
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/shared/providers/store';
import type { ThemeMode, ColorTheme, ResolvedTheme, ThemeConfig } from '@/shared/types/theme.types';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface ThemeState {
  mode: ThemeMode;
  colorTheme: ColorTheme;
  currentTheme: ResolvedTheme;
  systemPreference: ResolvedTheme;
  initialized: boolean;
  config: ThemeConfig;
  isTransitioning: boolean;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: ThemeState = {
  // Flagship night desk is the live product default (mechagora.flagship.theme)
  mode: 'dark',
  colorTheme: 'mechagora',
  currentTheme: 'dark',
  systemPreference: 'light',
  initialized: false,
  config: {
    enableSmartTheme: true,
    enableTransitions: true,
  },
  isTransitioning: false,
};

// =============================================================================
// SLICE
// =============================================================================

export const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      // Update current theme based on mode
      if (action.payload === 'auto') {
        state.currentTheme = state.systemPreference;
      } else {
        state.currentTheme = action.payload as ResolvedTheme;
      }
    },
    toggle: (state) => {
      // Leave auto/explicit into the opposite of what is currently on screen
      // (currentTheme), not a stale systemPreference default.
      const resolved: ResolvedTheme =
        state.mode === 'auto' ? state.currentTheme : (state.mode as ResolvedTheme);
      const next: ResolvedTheme = resolved === 'dark' ? 'light' : 'dark';
      state.mode = next;
      state.currentTheme = next;
    },
    setColorTheme: (state, action: PayloadAction<ColorTheme>) => {
      state.colorTheme = action.payload;
    },
    setSystemPreference: (state, action: PayloadAction<ResolvedTheme>) => {
      state.systemPreference = action.payload;
      // Update current theme if in auto mode
      if (state.mode === 'auto') {
        state.currentTheme = action.payload;
      }
    },
    updateConfig: (state, action: PayloadAction<Partial<ThemeConfig>>) => {
      state.config = {
        ...state.config,
        ...action.payload,
      };
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
    },
    setTransitioning: (state, action: PayloadAction<boolean>) => {
      state.isTransitioning = action.payload;
    },
    resetTheme: (state) => {
      Object.assign(state, initialState);
    },
  },
});

// =============================================================================
// ACTIONS
// =============================================================================

export const {
  setMode,
  toggle,
  setColorTheme,
  setSystemPreference,
  updateConfig,
  setInitialized,
  setTransitioning,
  resetTheme,
} = themeSlice.actions;

// =============================================================================
// SELECTORS
// =============================================================================

export const selectThemeMode = (state: RootState) => state.theme?.mode ?? 'auto';
export const selectColorTheme = (state: RootState) => state.theme?.colorTheme ?? 'mechagora';
export const selectCurrentTheme = (state: RootState) => state.theme?.currentTheme ?? 'dark';
export const selectSystemPreference = (state: RootState) => state.theme?.systemPreference ?? 'dark';
export const selectThemeInitialized = (state: RootState) => state.theme?.initialized ?? false;
export const selectThemeConfig = (state: RootState) => state.theme?.config ?? initialState.config;
export const selectIsTransitioning = (state: RootState) => state.theme?.isTransitioning ?? false;

// Computed selectors
export const selectIsDark = (state: RootState) => selectCurrentTheme(state) === 'dark';
export const selectIsLight = (state: RootState) => selectCurrentTheme(state) === 'light';
export const selectIsAuto = (state: RootState) => selectThemeMode(state) === 'auto';

// =============================================================================
// REDUCER (default export)
// =============================================================================

export default themeSlice;
